import React, { useRef } from "react";
import { Drawer, Divider, List, Box, Hidden } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import clsx from "clsx";
import PropTypes from "prop-types";
import SideNavItem from "./SideNavItem";
import Logo from "../../Logo";
import { useIsIframed } from "../../../hooks/utils/iframeUtils";

const styles = theme => ({
  drawer: {
    flexShrink: 0
  },
  drawerPaper: {
    "-ms-overflow-style": "none",
    color: theme.palette.text.secondary,
    overflowX: "hidden",
    justifyContent: "space-between",
    zIndex: 12,
    [theme.breakpoints.down("sm")]: {
      minWidth: 200
    }
  },
  drawerExpanded: {
    width: theme.sizes.sideNavBarWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    "& ul > div > svg": {
      opacity: 1,
      transition: "opacity 0ms 175ms"
    }
  },
  drawerCollapsed: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: "hidden",
    width: theme.sizes.sideNavBarWidthCondensed,
    "& ul > div > svg": {
      opacity: 0,
      transitionProperty: "opacity"
    }
  },
  toolbar: {
    height: theme.sizes.topNavBarHeight,
    display: "flex",
    alignItems: "center",
    maxWidth: 200
  }
});

const SideNav = props => {
  const {
    classes,
    onMenuNavItemClick,
    mobileNavbarOpen,
    expanded,
    hideDivider,
    routes,
    logo,
    currentPath
  } = props;

  const menuRef = useRef();
  const isIframed = useIsIframed();

  const getMenuItemSection = section => {
    return (
      <List>
        {routes &&
          routes
            .filter(route => route.section === section && !route.hidden)
            .map((item, index) => (
              <SideNavItem
                menuExpanded={expanded}
                onMenuExpand={expandSideNav}
                item={item}
                key={index}
                onMenuNavItemClick={onMenuNavItemClick}
                currentPath={currentPath}
              />
            ))}
      </List>
    );
  };

  const getMenu = () => (
    <>
      <Box>
        <div className={classes.toolbar}>
          <Box display="flex" pt={0.5} px={2} width="100%" height="100%">
            <Logo logo={logo} />
          </Box>
        </div>
        {getMenuItemSection("upper")}
      </Box>
      <Box
        flex={1}
        data-qa="nav-sidenav-expand"
        onClick={expandSideNav}
        ref={menuRef}
      />
      <Box>
        {!hideDivider && <Divider />}
        {getMenuItemSection("lower")}
      </Box>
    </>
  );

  /**
   * Expands the collapsed side nav if the user clicked on the side nav outside of a menu item
   * or if the user clicked on an expandable menu item
   */
  const expandSideNav = (event, itemClicked) => {
    if (
      event.target === menuRef.current ||
      (itemClicked && itemClicked.children)
    ) {
      props.onExpandSideNav(!expanded);
    }
  };

  if (isIframed) {
    return null;
  }

  return (
    <div>
      <Hidden smUp>
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileNavbarOpen}
          onClose={onMenuNavItemClick}
          classes={{
            paper: classes.drawerPaper
          }}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
        >
          {getMenu()}
        </Drawer>
      </Hidden>
      <Hidden smDown implementation="css">
        <Drawer
          className={clsx(classes.drawer, {
            [classes.drawerExpanded]: expanded,
            [classes.drawerCollapsed]: !expanded
          })}
          variant="permanent"
          classes={{
            paper: clsx(classes.drawerPaper, {
              [classes.drawerExpanded]: expanded,
              [classes.drawerCollapsed]: !expanded
            })
          }}
          open={expanded}
          anchor="left"
        >
          {getMenu()}
        </Drawer>
      </Hidden>
    </div>
  );
};

export default withStyles(styles)(SideNav);

SideNav.propTypes = {
  mobileNavbarOpen: PropTypes.bool,
  expanded: PropTypes.bool,
  hideDivider: PropTypes.bool,
  routes: PropTypes.arrayOf(PropTypes.any),
  logo: PropTypes.node,
  currentPath: PropTypes.string,
  onMenuNavItemClick: PropTypes.func
};
